import React, { useState } from 'react';
import {
  Card,
  H4,
  HTMLSelect,
  Button,
  Intent,
  Tag
} from '@blueprintjs/core';

const PenaltyWidget = ({
  teams,
  penalties,
  onPenaltyChange,
  getTeamDisplayName
}) => {
  const [selectedTeam, setSelectedTeam] = useState(teams[0] || '');

  const applyPenalty = () => {
    if (!selectedTeam) return;

    const updatedPenalties = { ...penalties };
    
    // Initialize team penalties if not exists
    if (!updatedPenalties[selectedTeam]) {
      updatedPenalties[selectedTeam] = 0;
    }
    
    // Apply -1 penalty
    updatedPenalties[selectedTeam] -= 1;
    
    onPenaltyChange(updatedPenalties);
  };

  const clearPenalty = (teamId) => {
    const updatedPenalties = { ...penalties };
    
    if (updatedPenalties[teamId]) {
      delete updatedPenalties[teamId];
    }
    
    onPenaltyChange(updatedPenalties);
  };

  const getCurrentPenalties = () => {
    const penaltyList = [];
    
    Object.entries(penalties).forEach(([teamId, penalty]) => {
      if (penalty !== 0) {
        penaltyList.push({
          team: teamId,
          penalty
        });
      }
    });
    
    return penaltyList.sort((a, b) => a.team.localeCompare(b.team));
  };

  const getTeamPenaltyTotal = (teamId) => {
    if (!penalties[teamId]) return 0;
    return penalties[teamId];
  };

  const teamOptions = teams.map(team => ({
    label: getTeamDisplayName ? getTeamDisplayName(team) : `Team ${team}`,
    value: team
  }));

  return (
    <Card style={{ 
      padding: '1rem', 
      marginBottom: '1rem',
      background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-tertiary))',
      border: '1px solid var(--border)'
    }}>
      <H4 style={{ 
        margin: '0 0 1rem 0', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem' 
      }}>
        ⚠️ Penalties
      </H4>
      
      {/* Penalty Application */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr auto', 
        gap: '0.5rem', 
        alignItems: 'end',
        marginBottom: '1rem' 
      }}>
        <div>
          <label style={{ 
            fontSize: '0.8rem', 
            color: 'var(--text-secondary)', 
            marginBottom: '0.25rem',
            display: 'block' 
          }}>
            Team
          </label>
          <HTMLSelect
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            options={teamOptions}
            fill
          />
        </div>
        
        <Button
          intent={Intent.WARNING}
          onClick={applyPenalty}
          disabled={!selectedTeam}
          style={{ minWidth: '80px' }}
        >
          -1 Penalty
        </Button>
      </div>

      {/* Current Penalties List */}
      {getCurrentPenalties().length > 0 && (
        <div>
          <div style={{ 
            fontSize: '0.9rem', 
            fontWeight: 600, 
            marginBottom: '0.5rem',
            color: 'var(--text-secondary)' 
          }}>
            Active Penalties:
          </div>
          <div style={{ display: 'grid', gap: '0.25rem' }}>
            {getCurrentPenalties().map(({ team, penalty }) => (
              <div
                key={team}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem',
                  background: 'var(--bg-primary)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.85rem'
                }}
              >
                <span>
                  <strong>{getTeamDisplayName ? getTeamDisplayName(team) : `Team ${team}`}</strong>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Tag intent={Intent.WARNING} minimal>
                    {penalty} pts
                  </Tag>
                  <Button
                    icon="cross"
                    minimal
                    small
                    intent={Intent.DANGER}
                    onClick={() => clearPenalty(team)}
                    title="Remove penalty"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Penalty Totals Summary */}
      {Object.keys(penalties).length > 0 && (
        <div style={{ 
          marginTop: '1rem', 
          paddingTop: '1rem', 
          borderTop: '1px solid var(--border)' 
        }}>
          <div style={{ 
            fontSize: '0.9rem', 
            fontWeight: 600, 
            marginBottom: '0.5rem',
            color: 'var(--text-secondary)' 
          }}>
            Total Team Penalties:
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: '0.5rem' 
          }}>
            {teams.map(team => {
              const totalPenalty = getTeamPenaltyTotal(team);
              if (totalPenalty === 0) return null;
              
              return (
                <div
                  key={team}
                  style={{
                    textAlign: 'center',
                    padding: '0.5rem',
                    background: 'var(--bg-primary)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)'
                  }}
                >
                  <div style={{ 
                    fontSize: '0.8rem', 
                    color: 'var(--text-secondary)',
                    marginBottom: '0.25rem' 
                  }}>
                    {getTeamDisplayName ? getTeamDisplayName(team) : `Team ${team}`}
                  </div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: 700, 
                    color: 'var(--warning)' 
                  }}>
                    {totalPenalty} pts
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default PenaltyWidget;